import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Receipt } from "lucide-react";
import { PaymentAmount } from "./PaymentAmount";
import { SecurityBadges } from "./SecurityBadges";
import { PaymentStatus } from "./PaymentStatus";
import { RecentPayments } from "./RecentPayments";
import { supabase } from "@/integrations/supabase/client";
import { useFormContext } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";

interface PaymentSectionProps {
  amount: number;
  taxReturnId: string;
  serviceId: string;
  onPaymentSuccess: (paymentIntentId: string) => void;
}

export function PaymentSection({ amount, taxReturnId, serviceId, onPaymentSuccess }: PaymentSectionProps) {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'ready' | 'processing' | 'success' | 'error'>('ready');
  const [recentPayments, setRecentPayments] = useState<any[]>([]);
  const form = useFormContext();
  const { toast } = useToast();

  const initializePayment = async () => {
    try {
      setStatus('processing');
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user profile for customer details
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email, client_number')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile) throw new Error('Profile not found');

      // Create checkout session
      const { data, error: checkoutError } = await supabase.functions.invoke(
        'create-tax-return-checkout',
        {
          body: {
            taxReturnId,
            serviceId,
            amount: amount * 100, // Convert to cents for Stripe
            customerDetails: {
              userId: user.id,
              name: profile.full_name,
              email: profile.email,
              clientNumber: profile.client_number
            },
            metadata: {
              reference: `TR-${taxReturnId}`
            }
          }
        }
      );

      if (checkoutError) throw checkoutError;
      if (!data?.url) throw new Error('No checkout URL received');

      // Redirect to Stripe Checkout
      window.location.href = data.url;
      setStatus('success');

    } catch (err) {
      console.error('Payment initialization error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initiate payment');
      setStatus('error');
      toast({
        title: "Payment Error",
        description: "There was a problem initiating the payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    async function fetchRecentPayments() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('payment_transactions')
        .select(`
          created_at,
          amount,
          status,
          tax_return_requests (
            financial_year,
            service_type
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(3);

      if (!error && data) {
        setRecentPayments(data);
      }
    }

    fetchRecentPayments();
  }, []);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-6">
        <PaymentAmount amount={amount} />
        <SecurityBadges />
        <PaymentStatus status={status} />
        <button
          onClick={initializePayment}
          disabled={status === 'processing'}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2"
        >
          {status === 'processing' ? (
            <>Processing...</>
          ) : (
            <>
              <Receipt className="w-4 h-4 mr-2" />
              Pay Now
            </>
          )}
        </button>
        <RecentPayments payments={recentPayments} />
      </CardContent>
    </Card>
  );
}
